 

import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  FaPlay,
  FaPause,
  FaStepForward,
  FaStepBackward,
  FaRedo,
  FaInfoCircle,
  FaCode,
  FaBook,
  FaChartBar,
} from "react-icons/fa"
import { MdSpeed, MdOutlineEmojiObjects, MdCompareArrows } from "react-icons/md"
import { BsEmojiSmile, BsEmojiFrown, BsArrowDown } from "react-icons/bs"
import { IoMdCheckmarkCircleOutline } from "react-icons/io"
// import Confetti from "react-confetti"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism"
import "../styles/algorithm-visualiser.css"

const AlgorithmVisualizer = () => {
  // State variables
  const [text, setText] = useState("ABABCABABCABDAB")
  const [pattern, setPattern] = useState("ABCABD")
  const [speed, setSpeed] = useState(1)
  const [isRunning, setIsRunning] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [totalSteps, setTotalSteps] = useState(0)
  const [logs, setLogs] = useState([])
  const [matches, setMatches] = useState([])
  const [activeTab, setActiveTab] = useState("visualization")
  const [showConfetti, setShowConfetti] = useState(false)
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 })
  const [currentExplanation, setCurrentExplanation] = useState("")
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipContent, setTooltipContent] = useState("")
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [difficulty, setDifficulty] = useState("medium")
  const [showCodeExplanation, setShowCodeExplanation] = useState(false)
  const [showConclusion, setShowConclusion] = useState(false)
  const [conclusionData, setConclusionData] = useState({
    totalComparisons: 0,
    characterComparisons: 0,
    trueMatches: 0,
    executionTime: 0,
  })
  const [allSteps, setAllSteps] = useState([])
  const [currentComparison, setCurrentComparison] = useState(null)

  // Refs
  const animationRef = useRef(null)
  const containerRef = useRef(null)
  const stepTimeoutRef = useRef(null)
  const startTimeRef = useRef(null)

  // Update window size for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Clear animation on unmount
  useEffect(() => {
    return () => {
      if (stepTimeoutRef.current) {
        clearTimeout(stepTimeoutRef.current)
      }
    }
  }, [])

  // Reset visualization
  const resetVisualization = () => {
    if (stepTimeoutRef.current) {
      clearTimeout(stepTimeoutRef.current)
    }
    setIsRunning(false)
    setIsPaused(false)
    setCurrentStep(0)
    setLogs([])
    setMatches([])
    setShowConfetti(false)
    setCurrentExplanation("")
    setCurrentComparison(null)
    setShowConclusion(false)
    setAllSteps([])
    setConclusionData({
      totalComparisons: 0,
      characterComparisons: 0,
      trueMatches: 0,
      executionTime: 0,
    })
  }

  // Get explanation for the current step
  const getExplanation = (step, position, isMatch, charComparisons) => {
    if (step === 0) {
      return `We start by comparing the pattern "${pattern}" with the first window of text "${text.substring(0, pattern.length)}". We compare each character one by one.`
    } else if (isMatch) {
      return `🎉 Match found! Character-by-character verification confirms that the substring at position ${position} matches our pattern "${pattern}". This is a true positive match after comparing ${charComparisons} characters.`
    } else {
      return `We slide the window to position ${position} and compare the pattern "${pattern}" with the substring "${text.substring(position, position + pattern.length)}". The comparison failed after checking ${charComparisons} characters.`
    }
  }

  // Get emoji based on the current state
  const getEmoji = (isMatch) => {
    if (isMatch) return <BsEmojiSmile className="emoji happy" />
    return <BsEmojiFrown className="emoji sad" />
  }

  // Run the Naive String Matching algorithm
  const runNaiveStringMatching = () => {
    resetVisualization()
    setIsRunning(true)
    startTimeRef.current = performance.now()

    const m = pattern.length
    const n = text.length

    if (m > n) {
      setLogs(["Pattern is longer than text. No matches possible."])
      setIsRunning(false)
      return
    }

    const addLog = (message) => {
      setLogs((prevLogs) => [...prevLogs, message])
    }

    addLog(`Starting naive string matching for pattern "${pattern}" in text "${text}"`)

    // Prepare all steps in advance
    const steps = []
    let totalCharComparisons = 0
    let trueMatches = 0

    for (let i = 0; i <= n - m; i++) {
      const currentWindow = text.substring(i, i + m)
      let j
      let charComparisons = 0
      const comparisonResults = []

      // Compare each character of pattern with the current window
      for (j = 0; j < m; j++) {
        charComparisons++
        totalCharComparisons++

        // Store the result of each character comparison
        comparisonResults.push({
          patternChar: pattern[j],
          textChar: text[i + j],
          isMatch: pattern[j] === text[i + j],
          patternIndex: j,
          textIndex: i + j,
        })

        if (pattern[j] !== text[i + j]) {
          break
        }
      }

      const isFullMatch = j === m
      if (isFullMatch) trueMatches++

      steps.push({
        position: i,
        currentWindow,
        isFullMatch,
        charComparisons,
        comparisonResults,
        explanation: getExplanation(i, i, isFullMatch, charComparisons),
      })
    }

    setAllSteps(steps)
    setTotalSteps(steps.length)

    // Function to execute a single step
    const executeStep = (stepIndex) => {
      if (stepIndex >= steps.length) {
        const endTime = performance.now()
        const executionTime = ((endTime - startTimeRef.current) / 1000).toFixed(2)

        setConclusionData({
          totalComparisons: steps.length,
          characterComparisons: totalCharComparisons,
          trueMatches,
          executionTime,
        })

        setShowConclusion(true)
        setIsRunning(false)

        if (trueMatches > 0) {
          setShowConfetti(true)
          setTimeout(() => setShowConfetti(false), 5000)
        }
        return
      }

      const step = steps[stepIndex]
      setCurrentStep(stepIndex)

      addLog(
        `Window ${stepIndex + 1}: "${step.currentWindow}" (Position ${step.position}), Compared ${step.charComparisons} characters`,
      )

      setCurrentComparison({
        position: step.position,
        results: step.comparisonResults,
      })

      setCurrentExplanation(step.explanation)

      if (step.isFullMatch) {
        setMatches((prev) => [...prev, step.position])
      }

      // Schedule next step if not paused
      if (!isPaused) {
        const timeoutDuration = 2000 / speed
        stepTimeoutRef.current = setTimeout(() => {
          executeStep(stepIndex + 1)
        }, timeoutDuration)
      }
    }

    // Start execution with the first step
    executeStep(0)
  }

  // Pause or resume the animation
  const togglePause = () => {
    if (isPaused) {
      setIsPaused(false)
      // Resume from current step
      const timeoutDuration = 2000 / speed
      stepTimeoutRef.current = setTimeout(() => {
        executeStepByIndex(currentStep + 1)
      }, timeoutDuration)
    } else {
      setIsPaused(true)
      if (stepTimeoutRef.current) {
        clearTimeout(stepTimeoutRef.current)
      }
    }
  }

  // Execute a specific step by index
  const executeStepByIndex = (stepIndex) => {
    if (stepIndex < 0 || stepIndex >= allSteps.length) return

    if (stepTimeoutRef.current) {
      clearTimeout(stepTimeoutRef.current)
    }

    const step = allSteps[stepIndex]
    setCurrentStep(stepIndex)

    setCurrentComparison({
      position: step.position,
      results: step.comparisonResults,
    })

    setCurrentExplanation(step.explanation)

    // If this is a new match, add it
    if (step.isFullMatch && !matches.includes(step.position)) {
      setMatches((prev) => [...prev, step.position])
    }

    // Schedule next step if not paused and not at the end
    if (!isPaused && stepIndex < allSteps.length - 1) {
      const timeoutDuration = 2000 / speed
      stepTimeoutRef.current = setTimeout(() => {
        executeStepByIndex(stepIndex + 1)
      }, timeoutDuration)
    } else if (stepIndex >= allSteps.length - 1) {
      // We've reached the end
      const endTime = performance.now()
      const executionTime = ((endTime - startTimeRef.current) / 1000).toFixed(2)

      setConclusionData({
        totalComparisons: allSteps.length,
        characterComparisons: allSteps.reduce((sum, step) => sum + step.charComparisons, 0),
        trueMatches: allSteps.filter((s) => s.isFullMatch).length,
        executionTime,
      })

      setShowConclusion(true)
      setIsRunning(false)

      if (allSteps.some((s) => s.isFullMatch)) {
        setShowConfetti(true)
        setTimeout(() => setShowConfetti(false), 5000)
      }
    }
  }

  // Step forward manually
  const stepForward = () => {
    if (currentStep < totalSteps - 1) {
      executeStepByIndex(currentStep + 1)
    }
  }

  // Step backward manually
  const stepBackward = () => {
    if (currentStep > 0) {
      executeStepByIndex(currentStep - 1)
    }
  }

  // Handle tooltip display
  const showComparisonTooltip = (comparison, event) => {
    const explanation = `Comparing pattern character '${comparison.patternChar}' at index ${comparison.patternIndex} with text character '${comparison.textChar}' at index ${comparison.textIndex}. ${comparison.isMatch ? "Characters match!" : "Characters do not match!"}`

    setTooltipContent(explanation)
    setTooltipPosition({ x: event.clientX, y: event.clientY })
    setShowTooltip(true)
  }

  const hideTooltip = () => {
    setShowTooltip(false)
  }

  // Display the text with highlighting
  const displayText = () => {
    return Array.from(text).map((char, index) => {
      const isHighlighted =
        matches.includes(index) || matches.some((matchPos) => index >= matchPos && index < matchPos + pattern.length)

      const isCurrentWindow =
        currentStep < totalSteps &&
        index >= allSteps[currentStep]?.position &&
        index < allSteps[currentStep]?.position + pattern.length

      const isCurrentlyComparing =
        currentComparison && currentComparison.results.some((comp) => comp.textIndex === index)

      const comparisonResult = currentComparison?.results.find((comp) => comp.textIndex === index)

      let className = "text-char"
      if (isHighlighted) className += " matched"
      if (isCurrentWindow) className += " current-window"
      if (isCurrentlyComparing && comparisonResult) {
        className += comparisonResult.isMatch ? " char-match" : " char-mismatch"
      }

      return (
        <motion.div
          key={index}
          className={className}
          initial={{ scale: 1 }}
          animate={
            isHighlighted
              ? {
                  scale: [1, 1.2, 1],
                  boxShadow: [
                    "0px 0px 0px rgba(255, 87, 34, 0)",
                    "0px 0px 10px rgba(255, 87, 34, 0.7)",
                    "0px 0px 0px rgba(255, 87, 34, 0)",
                  ],
                }
              : {}
          }
          transition={{ duration: 1, repeat: isHighlighted ? Number.POSITIVE_INFINITY : 0, repeatDelay: 1 }}
          onMouseEnter={
            isCurrentlyComparing && comparisonResult ? (e) => showComparisonTooltip(comparisonResult, e) : null
          }
          onMouseLeave={isCurrentlyComparing ? hideTooltip : null}
        >
          {char}
        </motion.div>
      )
    })
  }

  // Display pattern with comparison to current window
  const displayPatternComparison = () => {
    if (currentStep >= totalSteps || !allSteps[currentStep] || !currentComparison) return null

    const currentWindowStart = allSteps[currentStep]?.position || 0
    const currentWindow = text.substring(currentWindowStart, currentWindowStart + pattern.length)

    return (
      <div className="pattern-comparison">
        <div className="comparison-header">
          <h4>Pattern Comparison</h4>
          <div className="comparison-status">
            {allSteps[currentStep]?.isFullMatch ? (
              <span className="match-status success">
                <IoMdCheckmarkCircleOutline /> Match
              </span>
            ) : (
              <span className="match-status neutral">
                <MdCompareArrows /> Comparing
              </span>
            )}
          </div>
        </div>

        <div className="comparison-content">
          <div className="comparison-row">
            <div className="comparison-label">Pattern:</div>
            <div className="comparison-chars">
              {Array.from(pattern).map((char, index) => {
                const comparisonResult = currentComparison.results.find((comp) => comp.patternIndex === index)
                const isCompared = comparisonResult !== undefined
                const isMatch = isCompared && comparisonResult.isMatch

                return (
                  <div
                    key={`pattern-${index}`}
                    className={`comparison-char ${isCompared ? (isMatch ? "match" : "mismatch") : ""}`}
                  >
                    {char}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="comparison-arrows">
            {Array.from(pattern).map((_, index) => {
              const comparisonResult = currentComparison.results.find((comp) => comp.patternIndex === index)
              const isCompared = comparisonResult !== undefined
              const isMatch = isCompared && comparisonResult.isMatch

              return (
                <div key={`arrow-${index}`} className="comparison-arrow">
                  {isCompared ? (
                    <BsArrowDown className={isMatch ? "arrow-match" : "arrow-mismatch"} />
                  ) : (
                    <span className="arrow-placeholder"></span>
                  )}
                </div>
              )
            })}
          </div>

          <div className="comparison-row">
            <div className="comparison-label">Window:</div>
            <div className="comparison-chars">
              {Array.from(currentWindow).map((char, index) => {
                const comparisonResult = currentComparison.results.find((comp) => comp.patternIndex === index)
                const isCompared = comparisonResult !== undefined
                const isMatch = isCompared && comparisonResult.isMatch

                return (
                  <div
                    key={`window-${index}`}
                    className={`comparison-char ${isCompared ? (isMatch ? "match" : "mismatch") : ""}`}
                  >
                    {char}
                  </div>
                )
              })}
            </div>
            <div className="window-position">Position: {allSteps[currentStep]?.position}</div>
          </div>
        </div>
      </div>
    )
  }

  // Display algorithm conclusion
  const displayConclusion = () => {
    if (!showConclusion) return null

    return (
      <motion.div
        className="algorithm-conclusion"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h3>Algorithm Execution Summary</h3>
        <div className="conclusion-content">
          <div className="conclusion-stats">
            <div className="nsstat-item">
              <div className="nsstat-value">{conclusionData.totalComparisons}</div>
              <div className="nsstat-label">Total Windows Compared</div>
            </div>
            <div className="nsstat-item">
              <div className="nsstat-value">{conclusionData.characterComparisons}</div>
              <div className="nsstat-label">Character Comparisons</div>
            </div>
            <div className="nsstat-item">
              <div className="nsstat-value">{conclusionData.trueMatches}</div>
              <div className="nsstat-label">True Matches</div>
            </div>
            <div className="nsstat-item">
              <div className="nsstat-value">{conclusionData.executionTime}s</div>
              <div className="nsstat-label">Execution Time</div>
            </div>
          </div>

          <div className="conclusion-analysis">
            <h4>Analysis</h4>
            <p>
              The Naive String Matching algorithm found {conclusionData.trueMatches} match
              {conclusionData.trueMatches !== 1 ? "es" : ""} in the text. It performed {conclusionData.totalComparisons}{" "}
              window comparisons and a total of {conclusionData.characterComparisons} character comparisons.
            </p>
            <p>
              The naive approach compares each character of the pattern with each possible substring of the text. This
              results in a worst-case time complexity of O(n*m), where n is the length of the text and m is the length
              of the pattern.
            </p>
            <p>
              While simple to implement, the naive algorithm can be inefficient for large texts or patterns. More
              advanced algorithms like KMP, Boyer-Moore, or Rabin-Karp can provide better performance by avoiding
              redundant comparisons.
            </p>
          </div>
        </div>
      </motion.div>
    )
  }

  // Algorithm code with syntax highlighting
  const algorithmCode = `function naiveStringMatching(text, pattern) {
  const n = text.length;
  const m = pattern.length;
  const matches = [];
  
  // Slide the pattern over text one by one
  for (let i = 0; i <= n - m; i++) {
    let j;
    
    // For current index i, check for pattern match
    for (j = 0; j < m; j++) {
      // If characters don't match, break
      if (text[i + j] !== pattern[j]) {
        break;
      }
    }
    
    // If pattern was fully matched, j will equal m
    if (j === m) {
      matches.push(i);
    }
  }
  
  return matches;
}`

  // Code explanation sections
  const codeExplanations = [
    {
      title: "Brute Force Approach",
      content:
        "The naive string matching algorithm uses a straightforward brute force approach. It checks every possible position in the text where the pattern could match.",
    },
    {
      title: "Character-by-Character Comparison",
      content:
        "For each position, it compares the pattern with the substring of the text character by character. If any character doesn't match, it immediately moves to the next position.",
    },
    {
      title: "Early Termination",
      content:
        "The algorithm stops comparing characters as soon as it finds a mismatch, which can save some comparisons but doesn't change the worst-case complexity.",
    },
  ]

  return (
    <div className="NSalgorithm-visualizer" ref={containerRef}>
      {/* {showConfetti && (
        <Confetti width={windowSize.width} height={windowSize.height} recycle={false} numberOfPieces={200} />
      )} */}

      <header className="visualizer-header">
        <div className="logo">
          {/* <MdOutlineEmojiObjects className="logo-icon" /> */}
          {/* <h1>AlgoViz</h1> */}
        </div>
        <div className="algorithm-title">
          <h2>Naive String Matching Algorithm</h2>
        </div>
        {/* <div className="difficulty-selector"> */}
          {/* <span>Difficulty:</span> */}
          {/* <select */}
            {/* value={difficulty} */}
            {/* onChange={(e) => setDifficulty(e.target.value)} */}
            {/* className={`difficulty-${difficulty}`} */}
          
            {/* <option value="easy">Easy 😊</option>
            <option value="medium">Medium 😐</option>
            <option value="hard">Hard 🤔</option> */}
          {/* </select> */}
        {/* </div> */}
      </header>

      <div className="visualizer-content">
        <nav className="visualizer-nav">
          <button
            className={`nav-button ${activeTab === "visualization" ? "active" : ""}`}
            onClick={() => setActiveTab("visualization")}
          >
            <FaPlay /> Visualization
          </button>
          <button
            className={`nav-button ${activeTab === "algorithm" ? "active" : ""}`}
            onClick={() => setActiveTab("algorithm")}
          >
            <FaBook /> Algorithm
          </button>
          <button className={`nav-button ${activeTab === "code" ? "active" : ""}`} onClick={() => setActiveTab("code")}>
            <FaCode /> Code
          </button>
          <button
            className={`nav-button ${activeTab === "complexity" ? "active" : ""}`}
            onClick={() => setActiveTab("complexity")}
          >
            <FaChartBar /> Complexity
          </button>
        </nav>

        <main className="visualizer-main">
          <AnimatePresence mode="wait">
            {activeTab === "visualization" && (
              <motion.div
                key="visualization"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="visualization-tab"
              >
                <div className="nsinput-section">
                  <div className="nsinput-group">
                    <label htmlFor="text-nsinput">
                      <span>Text:</span>
                      <FaInfoCircle className="info-icon" title="Enter the text to search within" />
                    </label>
                    <input
                      id="text-nsinput"
                      type="text"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      disabled={isRunning}
                      placeholder="Enter text to search in"
                    />
                  </div>

                  <div className="nsinput-group">
                    <label htmlFor="pattern-nsinput">
                      <span>Pattern:</span>
                      <FaInfoCircle className="info-icon" title="Enter the pattern to search for" />
                    </label>
                    <input
                      id="pattern-nsinput"
                      type="text"
                      value={pattern}
                      onChange={(e) => setPattern(e.target.value)}
                      disabled={isRunning}
                      placeholder="Enter pattern to search for"
                    />
                  </div>

                  <div className="nsinput-group speed-group">
                    <label htmlFor="speed-nsinput">
                      <MdSpeed className="speed-icon" />
                      <span>Speed:</span>
                    </label>
                    <div className="speed-control">
                      <input
                        id="speed-nsinput"
                        type="range"
                        min="0.5"
                        max="3"
                        step="0.5"
                        value={speed}
                        onChange={(e) => setSpeed(Number.parseFloat(e.target.value))}
                        disabled={isRunning && !isPaused}
                      />
                      <span className="speed-value">{speed}x</span>
                    </div>
                  </div>
                </div>

                <div className="controls-section">
                  <button
                    className="control-button play-button"
                    onClick={runNaiveStringMatching}
                    disabled={isRunning && !isPaused}
                  >
                    <FaPlay />
                    <span>Start</span>
                  </button>

                  <button className="control-button pause-button" onClick={togglePause} disabled={!isRunning}>
                    <FaPause />
                    <span>{isPaused ? "Resume" : "Pause"}</span>
                  </button>

                  <button
                    className="control-button step-button"
                    onClick={stepBackward}
                    disabled={!isPaused || currentStep <= 0}
                  >
                    <FaStepBackward />
                    <span>Previous</span>
                  </button>

                  <button
                    className="control-button step-button"
                    onClick={stepForward}
                    disabled={!isPaused || currentStep >= totalSteps - 1}
                  >
                    <FaStepForward />
                    <span>Next</span>
                  </button>

                  <button
                    className="control-button reset-button"
                    onClick={resetVisualization}
                    disabled={!isRunning && currentStep === 0}
                  >
                    <FaRedo />
                    <span>Reset</span>
                  </button>
                </div>

                <div className="visualization-section">
                  <h3>Text Visualization</h3>
                  <div className="text-display">{displayText()}</div>

                  {showTooltip && (
                    <div
                      className="tooltip"
                      style={{
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y - 100}px`,
                      }}
                    >
                      {tooltipContent}
                    </div>
                  )}

                  <div className="visualization-flex-container">
                    <div className="visualization-main-column">
                      {/* Pattern comparison section */}
                      {displayPatternComparison()}

                      <div className="explanation-box">
                        <div className="explanation-header">
                          <h3>Current Step Explanation</h3>
                          {currentStep < totalSteps &&
                            allSteps[currentStep]?.isFullMatch !== undefined &&
                            getEmoji(allSteps[currentStep]?.isFullMatch)}
                        </div>
                        <div className="explanation-content">
                          {currentExplanation || "Click 'Start' to begin the visualization."}
                        </div>
                      </div>
                    </div>

                    <div className="visualization-side-column">
                      <div className="naive-explanation">
                        <h4>Naive String Matching</h4>
                        <p>
                          The naive string matching algorithm compares the pattern with every possible substring of the
                          text.
                        </p>
                        <p>
                          For each position in the text, it compares the characters of the pattern with the characters
                          of the text one by one.
                        </p>
                        <p>
                          If all characters match, it records the position as a match. If any character doesn't match,
                          it immediately moves to the next position.
                        </p>
                        <div className="naive-steps">
                          <h5>Algorithm Steps:</h5>
                          <ol>
                            <li>Slide the pattern over the text one position at a time</li>
                            <li>For each position, compare the pattern with the substring character by character</li>
                            <li>If all characters match, record the position as a match</li>
                            <li>If any character doesn't match, move to the next position</li>
                            <li>Continue until the end of the text is reached</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Algorithm conclusion */}
                {displayConclusion()}

                <div className="results-section">
                  {matches.length > 0 && (
                    <div className="matches-display">
                      <h3>Matches Found: {matches.length}</h3>
                      <ul className="matches-list">
                        {matches.map((pos, idx) => (
                          <motion.li
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="match-item"
                          >
                            <span className="match-position">Position {pos}:</span>
                            <span className="match-text">"{text.substring(pos, pos + pattern.length)}"</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="logs-display">
                    <h3>Algorithm Steps</h3>
                    <div className="logs-content">
                      {logs.length === 0 ? (
                        <div className="empty-logs">Click 'Start' to see the algorithm in action</div>
                      ) : (
                        logs.map((log, index) => (
                          <motion.div
                            key={index}
                            className="log-entry"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <span className="log-number">{index + 1}.</span>
                            <span className="log-message">{log}</span>
                          </motion.div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === "algorithm" && (
              <motion.div
                key="algorithm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="algorithm-tab"
              >
                <div className="algorithm-content">
                  <h3>How Naive String Matching Algorithm Works</h3>

                  <div className="algorithm-section">
                    <h4>Overview</h4>
                    <p>
                      The Naive String Matching algorithm is the simplest approach to find occurrences of a pattern
                      within a text. It works by checking each position in the text as a potential starting point for
                      the pattern, and then comparing the characters one by one.
                    </p>
                  </div>

                  <div className="algorithm-section">
                    <h4>Key Steps</h4>
                    <ol className="steps-list">
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                      >
                        <strong>Initialization:</strong> Start at the first position of the text.
                      </motion.li>
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        <strong>Character Comparison:</strong> Compare each character of the pattern with the
                        corresponding character in the text.
                      </motion.li>
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <strong>Match Check:</strong> If all characters match, record the position as a match.
                      </motion.li>
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                      >
                        <strong>Early Termination:</strong> If any character doesn't match, stop comparing and move to
                        the next position.
                      </motion.li>
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 }}
                      >
                        <strong>Slide and Repeat:</strong> Slide the pattern one position to the right and repeat steps
                        2-4.
                      </motion.li>
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                      >
                        <strong>Completion:</strong> Continue until the end of the text is reached.
                      </motion.li>
                    </ol>
                  </div>

                  <div className="algorithm-section">
                    <h4>Example</h4>
                    <p>Let's trace through a simple example:</p>
                    <div className="example-trace">
                      <p>
                        <strong>Text:</strong> "ABABCABABCABDAB"
                      </p>
                      <p>
                        <strong>Pattern:</strong> "ABCABD"
                      </p>

                      <div className="example-steps">
                        <div className="example-step">
                          <p>
                            <strong>Position 0:</strong>
                          </p>
                          <div className="example-comparison">
                            <div className="example-text">ABABCABABCABDAB</div>
                            <div className="example-pattern">ABCABD</div>
                            <div className="example-markers">^^ (Mismatch at position 2)</div>
                          </div>
                        </div>

                        <div className="example-step">
                          <p>
                            <strong>Position 1:</strong>
                          </p>
                          <div className="example-comparison">
                            <div className="example-text">ABABCABABCABDAB</div>
                            <div className="example-pattern"> ABCABD</div>
                            <div className="example-markers"> ^ (Mismatch at position 0)</div>
                          </div>
                        </div>

                        <div className="example-step">
                          <p>
                            <strong>Position 2:</strong>
                          </p>
                          <div className="example-comparison">
                            <div className="example-text">ABABCABABCABDAB</div>
                            <div className="example-pattern"> ABCABD</div>
                            <div className="example-markers"> ^^^ (Mismatch at position 3)</div>
                          </div>
                        </div>

                        <p>...and so on until all positions are checked.</p>
                      </div>
                    </div>
                  </div>

                  <div className="algorithm-section">
                    <h4>Applications</h4>
                    <ul className="applications-list">
                      <li>Text editors for "find" functionality</li>
                      <li>Simple search operations in databases</li>
                      <li>Pattern matching in DNA sequences (for small patterns)</li>
                      <li>Spell checkers</li>
                      <li>Command-line utilities like grep (for simple cases)</li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === "code" && (
              <motion.div
                key="code"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="code-tab"
              >
                <div className="code-content">
                  <div className="code-header">
                    <h3>Naive String Matching Implementation</h3>
                    <button className="explanation-toggle" onClick={() => setShowCodeExplanation(!showCodeExplanation)}>
                      {showCodeExplanation ? "Hide Explanation" : "Show Explanation"}
                    </button>
                  </div>

                  <div className="code-container">
                    <SyntaxHighlighter
                      language="javascript"
                      style={vscDarkPlus}
                      showLineNumbers={true}
                      wrapLines={true}
                    >
                      {algorithmCode}
                    </SyntaxHighlighter>
                  </div>

                  {showCodeExplanation && (
                    <div className="code-explanation">
                      {codeExplanations.map((section, index) => (
                        <motion.div
                          key={index}
                          className="explanation-section"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <h4>{section.title}</h4>
                          <p>{section.content}</p>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === "complexity" && (
              <motion.div
                key="complexity"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="complexity-tab"
              >
                <div className="complexity-content">
                  <h3>Time and Space Complexity</h3>

                  <div className="complexity-grid">
                    <div className="complexity-card best-case">
                      <h4>Best Case</h4>
                      <div className="complexity-value">O(n)</div>
                      <p>
                        Where n is the length of the text. This occurs when the first character of the pattern doesn't
                        match any character in the text, allowing immediate skipping to the next position.
                      </p>
                    </div>

                    <div className="complexity-card average-case">
                      <h4>Average Case</h4>
                      <div className="complexity-value">O(n × m)</div>
                      <p>
                        Where n is the length of the text and m is the length of the pattern. On average, we expect to
                        compare a few characters at each position before finding a mismatch.
                      </p>
                    </div>

                    <div className="complexity-card worst-case">
                      <h4>Worst Case</h4>
                      <div className="complexity-value">O(n × m)</div>
                      <p>
                        This occurs when we need to compare all characters of the pattern at each position, such as when
                        the text is all 'A's and the pattern is all 'A's except the last character.
                      </p>
                    </div>

                    <div className="complexity-card space-complexity">
                      <h4>Space Complexity</h4>
                      <div className="complexity-value">O(1)</div>
                      <p>
                        The naive algorithm uses a constant amount of extra space regardless of input size. This makes
                        it memory-efficient for large texts.
                      </p>
                    </div>
                  </div>

                  <div className="comparison-section">
                    <h4>Comparison with Other String Matching Algorithms</h4>
                    <div className="comparison-table-container">
                      <table className="comparison-table">
                        <thead>
                          <tr>
                            <th>Algorithm</th>
                            <th>Preprocessing</th>
                            <th>Matching</th>
                            <th>Space</th>
                            <th>Best For</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="highlighted-row">
                            <td>Naive</td>
                            <td>None</td>
                            <td>O(n × m)</td>
                            <td>O(1)</td>
                            <td>Short patterns, simple cases</td>
                          </tr>
                          <tr>
                            <td>Rabin-Karp</td>
                            <td>O(m)</td>
                            <td>O(n + m) to O(n × m)</td>
                            <td>O(1)</td>
                            <td>Multiple pattern search</td>
                          </tr>
                          <tr>
                            <td>KMP</td>
                            <td>O(m)</td>
                            <td>O(n)</td>
                            <td>O(m)</td>
                            <td>Single pattern search</td>
                          </tr>
                          <tr>
                            <td>Boyer-Moore</td>
                            <td>O(m + σ)</td>
                            <td>O(n/m) to O(n × m)</td>
                            <td>O(m + σ)</td>
                            <td>Large alphabets</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="table-note">σ represents the size of the alphabet</p>
                  </div>

                  <div className="algorithm-section">
                    <h4>Inefficiencies of Naive Approach</h4>
                    <p>The naive algorithm has several inefficiencies that more advanced algorithms address:</p>
                    <ul>
                      <li>
                        <strong>Redundant Comparisons:</strong> It may compare the same characters multiple times.
                      </li>
                      <li>
                        <strong>No Preprocessing:</strong> It doesn't use any information from previous comparisons.
                      </li>
                      <li>
                        <strong>Limited Skipping:</strong> It always moves just one position at a time, even when larger
                        jumps would be safe.
                      </li>
                      <li>
                        <strong>Poor Performance on Repetitive Patterns:</strong> It performs poorly when the pattern
                        has repeating subpatterns.
                      </li>
                    </ul>
                    <p>
                      Despite these inefficiencies, the naive algorithm is still useful for its simplicity and low
                      memory usage, especially for short patterns or when the text is not highly repetitive.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      <footer className="visualizer-footer">
        <p>© 2025 AlgoViz - Interactive Algorithm Visualization Platform</p>
      </footer>
    </div>
  )
}

export default AlgorithmVisualizer
